import { BaseModel, type RelationConfig } from './base';
import { OrderItem } from './orderItem';
import { Tax } from './tax';
import { PaymentRequest } from './paymentRequest';

export enum OrderTipType {
  single = 0,
  perEmployee = 1,
}

export const OrderTipTypeLabels: Record<OrderTipType, string> = {
  [OrderTipType.single]: 'api.models.order.tipSingle',
  [OrderTipType.perEmployee]: 'api.models.order.tipPerEmployee',
};

export enum OrderTerminalPaymentStatus {
  pending = 'pending',
  processing = 'processing',
  complete = 'complete',
  cancelled = 'cancelled',
  timeout = 'timeout',
}

export const OrderTerminalPaymentStatusLabels: Record<OrderTerminalPaymentStatus, string> = {
  [OrderTerminalPaymentStatus.pending]: 'general.pending',
  [OrderTerminalPaymentStatus.processing]: 'general.processing',
  [OrderTerminalPaymentStatus.complete]: 'general.complete',
  [OrderTerminalPaymentStatus.cancelled]: 'general.cancelled',
  [OrderTerminalPaymentStatus.timeout]: 'api.models.order.timeout',
};

export class OrderDataTotals extends BaseModel {
  remainder!: number;
  total!: number;
  payments!: number;
  tip!: number;
}

export class OrderDataTaxTotal extends BaseModel {
  tax!: Tax;

  static override relations(): RelationConfig {
    return {
      tax: { type: Tax },
    };
  }
}

export class OrderDataItems extends BaseModel {
  reservations!: OrderItem[];
  products!: OrderItem[];
  charges!: OrderItem[];
  discounts!: OrderItem[];
  payments!: OrderItem[];

  static override relations(): RelationConfig {
    return {
      reservations: { type: OrderItem },
      products: { type: OrderItem },
      charges: { type: OrderItem },
      discounts: { type: OrderItem },
      payments: { type: OrderItem },
    };
  }
}

export class OrderData extends BaseModel {
  items!: OrderDataItems;
  totals!: OrderDataTotals;
  taxtotals!: OrderDataTaxTotal[];
  employeeTips!: OrderEmployeeTip[];

  static override relations(): RelationConfig {
    return {
      items: { type: OrderDataItems },
      totals: { type: OrderDataTotals },
      taxtotals: { type: OrderDataTaxTotal },
      employeeTips: { type: OrderEmployeeTip },
    };
  }
}

export class OrderEmployeeTip extends BaseModel {
  amount!: number;
  idEmployee!: number;
}

export class OrderListMonth extends BaseModel {
  orders!: Order[];

  static override relations(): RelationConfig {
    return {
      orders: { type: Order },
    };
  }
}

export class OrderTerminalPayment extends BaseModel {
  order!: Order;
  status!: OrderTerminalPaymentStatus;

  static override relations(): RelationConfig {
    return {
      order: { type: Order },
    };
  }
}

export class Order extends BaseModel {
  idOrder!: number;
  orderNumber!: string;
  notes!: string;
  tipType!: OrderTipType;
  priceLabel!: string;
  data!: OrderData;
  paymentRequests!: PaymentRequest[];

  static override relations(): RelationConfig {
    return {
      data: { type: OrderData },
      paymentRequests: { type: PaymentRequest },
    };
  }

  override objectID(): string | null {
    return this.idOrder?.toString() ?? null;
  }

  get hasServices(): boolean {
    return !!this.data?.items?.reservations?.length;
  }

  get hasProducts(): boolean {
    return !!this.data?.items?.products?.length;
  }

  get hasCharges(): boolean {
    return !!this.data?.items?.charges?.length;
  }

  get hasDiscounts(): boolean {
    return !!this.data?.items?.discounts?.length;
  }

  get hasPayments(): boolean {
    return !!this.data?.items?.payments?.length;
  }

  get hasItems(): boolean {
    return this.hasServices || this.hasProducts || this.hasCharges || this.hasDiscounts || this.hasPayments;
  }

  get hasRemaining(): boolean {
    return this.data?.totals?.remainder > 0;
  }

  get hasOverpayment(): boolean {
    return this.data?.totals?.total < this.data?.totals?.payments;
  }

  get hasCustomerCredit(): boolean {
    return this.data?.items?.products?.some(item => item.isCustomerCredit) ?? false;
  }

  get hasTip(): boolean {
    return this.data?.totals?.tip > 0;
  }

  get isTipTypeSingle(): boolean {
    return this.tipType === OrderTipType.single;
  }

  get isTipTypePerEmployee(): boolean {
    return this.tipType === OrderTipType.perEmployee;
  }
}
